import type { DropResult } from "@hello-pangea/dnd"
import { useCallback, useEffect, useState } from "react"

export function reorderArray<T>(
  items: T[],
  startIndex: number,
  endIndex: number,
): T[] {
  const result = Array.from(items)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

interface UseDragReorderOptions<T> {
  items: T[]
  onReordered: (newItems: T[]) => void
}

export function useDragReorder<T>({
  items,
  onReordered,
}: UseDragReorderOptions<T>) {
  const [orderedItems, setOrderedItems] = useState(items)

  useEffect(() => {
    setOrderedItems(items)
  }, [items])

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { destination, source } = result

      if (!destination || destination.index === source.index) {
        return
      }

      setOrderedItems((prev) => {
        const newItems = reorderArray(prev, source.index, destination.index)
        onReordered(newItems)
        return newItems
      })
    },
    [onReordered],
  )

  return { orderedItems, handleDragEnd }
}
